// Global map dock: React Context keeps map-related UI state; the map renders in a portal
// (see GlobalMapDock) when the panel is open. Map state (query, selection, radius) persists while navigating.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

type MapDockContextValue = {
  /** Bottom Fleet Map panel visibility (default closed). */
  mapDockOpen: boolean;
  setMapDockOpen: (open: boolean) => void;
  /** URL-style query string (no leading ?) for docked map; survives route changes */
  mapQuery: string;
  setMapQuery: (query: string) => void;
  selectedDeviceIds: string[];
  setSelectedDeviceIds: (ids: string[]) => void;
  toggleSelectedDeviceId: (id: string) => void;
  clearSelectedDeviceIds: () => void;
  radiusKm: number;
  setRadiusKm: (km: number) => void;
  radiusFilterEnabled: boolean;
  setRadiusFilterEnabled: (enabled: boolean) => void;
  radiusCenter: { lat: number; lng: number } | null;
  setRadiusCenter: (center: { lat: number; lng: number } | null) => void;
  baseVisibleDeviceIds: string[];
  setBaseVisibleDeviceIds: (ids: string[]) => void;
};

const MapDockContext = createContext<MapDockContextValue | null>(null);

export function MapDockProvider({ children }: { children: ReactNode }): JSX.Element {
  const DEFAULT_RADIUS_KM = 5;
  const [mapDockOpen, setMapDockOpen] = useState(false);
  const [mapQuery, setMapQuery] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIdsState] = useState<string[]>([]);
  const [radiusKm, setRadiusKmState] = useState<number>(DEFAULT_RADIUS_KM);
  const [radiusFilterEnabled, setRadiusFilterEnabledState] = useState<boolean>(false);
  const [radiusCenter, setRadiusCenterState] = useState<{ lat: number; lng: number } | null>(null);
  const [baseVisibleDeviceIds, setBaseVisibleDeviceIdsState] = useState<string[]>([]);

  const setSelectedDeviceIds = useCallback((ids: string[]) => {
    const deduped = Array.from(
      new Set(
        ids
          .map((id) => String(id ?? '').trim())
          .filter((id) => id.length > 0),
      ),
    );
    setSelectedDeviceIdsState(deduped);
  }, []);

  const toggleSelectedDeviceId = useCallback((id: string) => {
    const clean = String(id ?? '').trim();
    if (!clean) return;
    setSelectedDeviceIdsState((prev) =>
      prev.includes(clean) ? prev.filter((x) => x !== clean) : [...prev, clean],
    );
  }, []);

  const clearSelectedDeviceIds = useCallback(() => {
    setSelectedDeviceIdsState([]);
  }, []);

  const setRadiusKm = useCallback((km: number) => {
    if (!Number.isFinite(km) || km <= 0) return;
    setRadiusKmState(km);
  }, []);

  const setRadiusFilterEnabled = useCallback((enabled: boolean) => {
    setRadiusFilterEnabledState(Boolean(enabled));
  }, []);

  const setRadiusCenter = useCallback((center: { lat: number; lng: number } | null) => {
    if (!center) {
      setRadiusCenterState(null);
      return;
    }
    const lat = Number(center.lat);
    const lng = Number(center.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    setRadiusCenterState({ lat, lng });
  }, []);

  const setBaseVisibleDeviceIds = useCallback((ids: string[]) => {
    const cleaned = Array.from(
      new Set(
        ids
          .map((id) => String(id ?? '').trim())
          .filter((id) => id.length > 0),
      ),
    );
    setBaseVisibleDeviceIdsState(cleaned);
  }, []);

  const value = useMemo(
    () => ({
      mapDockOpen,
      setMapDockOpen,
      mapQuery,
      setMapQuery,
      selectedDeviceIds,
      setSelectedDeviceIds,
      toggleSelectedDeviceId,
      clearSelectedDeviceIds,
      radiusKm,
      setRadiusKm,
      radiusFilterEnabled,
      setRadiusFilterEnabled,
      radiusCenter,
      setRadiusCenter,
      baseVisibleDeviceIds,
      setBaseVisibleDeviceIds,
    }),
    [
      mapDockOpen,
      mapQuery,
      selectedDeviceIds,
      setSelectedDeviceIds,
      toggleSelectedDeviceId,
      clearSelectedDeviceIds,
      radiusKm,
      setRadiusKm,
      radiusFilterEnabled,
      setRadiusFilterEnabled,
      radiusCenter,
      setRadiusCenter,
      baseVisibleDeviceIds,
      setBaseVisibleDeviceIds,
    ],
  );

  return <MapDockContext.Provider value={value}>{children}</MapDockContext.Provider>;
}

export function useMapDock(): MapDockContextValue {
  const ctx = useContext(MapDockContext);
  if (!ctx) {
    throw new Error('useMapDock must be used within MapDockProvider');
  }
  return ctx;
}
